import { useQuery } from '@tanstack/react-query';
import type { ItemType } from '@brickloop/core/schemas';
import { api, qs, type Category, type Color, type ItemColoursResponse, type LocationRow, type Settings, type StocktakeSession, type StocktakeSessionSummary } from './api';

// Reference data changes rarely, so it is fetched once and kept.
const forever = { staleTime: Infinity } as const;

export const useColors = () =>
  useQuery({ queryKey: ['colors'], queryFn: () => api<{ colors: Color[] }>('/api/v1/catalog/colors').then((r) => r.colors), ...forever });

export const useCategories = () =>
  useQuery({
    queryKey: ['categories'],
    queryFn: () => api<{ categories: Category[] }>('/api/v1/catalog/categories').then((r) => r.categories),
    ...forever,
  });

export const useLocations = () =>
  useQuery({ queryKey: ['locations'], queryFn: () => api<{ locations: LocationRow[] }>('/api/v1/locations').then((r) => r.locations) });

export const useSettings = () =>
  useQuery({ queryKey: ['settings'], queryFn: () => api<{ settings: Settings }>('/api/v1/settings').then((r) => r.settings) });

/** Whether camera recognition is switched on. It is off unless the owner has turned it on. */
export const useRecognitionStatus = () =>
  useQuery({
    queryKey: ['recognition-status'],
    queryFn: () => api<{ enabled: boolean; provider: string | null }>('/api/v1/recognition/status'),
    staleTime: 60_000,
  });

/**
 * The colours a part comes in, asked of BrickLink each time. Deliberately not cached: no stale time, and the
 * answer is thrown away as soon as nothing is showing it, so every new part number is a fresh question.
 */
export const useItemColors = (itemType: ItemType, itemNo: string, enabled: boolean) =>
  useQuery({
    queryKey: ['item-colors', itemType, itemNo],
    enabled,
    staleTime: 0,
    gcTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    queryFn: () => api<ItemColoursResponse>(`/api/v1/bricklink/item-colors${qs({ itemType, itemNo })}`),
  });

export const useStocktakeSession = (id: number | undefined) =>
  useQuery({
    queryKey: ['stocktake', id],
    enabled: id !== undefined,
    queryFn: () => api<{ session: StocktakeSession }>(`/api/v1/stocktaking/${id}`).then((r) => r.session),
  });

export const useStocktakes = (locationId?: number) =>
  useQuery({
    queryKey: ['stocktakes', locationId],
    queryFn: () => api<{ sessions: StocktakeSessionSummary[] }>(`/api/v1/stocktaking${qs({ locationId })}`).then((r) => r.sessions),
  });

export const useSuppliers = () =>
  useQuery({
    queryKey: ['suppliers-all'],
    queryFn: () => api<{ suppliers: { id: number; name: string }[] }>('/api/v1/suppliers').then((r) => r.suppliers),
    staleTime: 60_000,
  });
